/**
 * Servicio API para gestión de Citas CRM
 * @module services/appointmentService
 */

import { api } from './api';
import type {
  Appointment,
  AppointmentCreate,
  AppointmentUpdate,
  AppointmentSlot,
  AppointmentListParams,
  AppointmentCalendarParams,
} from '@/types/appointment.types';

class AppointmentService {
  private readonly basePath = '/crm/appointments';

  /**
   * Crear nueva cita
   */
  async create(data: AppointmentCreate): Promise<Appointment> {
    const response = await api.post<Appointment>(this.basePath, data);
    return response.data;
  }

  /**
   * Listar citas con filtros.
   * Acepta respuesta en formato array directo o envuelto en { data } / { items }.
   */
  async list(params?: AppointmentListParams): Promise<Appointment[]> {
    const response = await api.get<Appointment[] | { data?: Appointment[]; items?: Appointment[] }>(
      this.basePath,
      { params }
    );
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: Appointment[] }).data))
      return (raw as { data: Appointment[] }).data;
    if (raw && typeof raw === 'object' && Array.isArray((raw as { items?: Appointment[] }).items))
      return (raw as { items: Appointment[] }).items;
    return [];
  }

  /**
   * Obtener cita por ID
   */
  async getById(id: string): Promise<Appointment> {
    const response = await api.get<Appointment>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Actualizar cita
   */
  async update(id: string, data: AppointmentUpdate): Promise<Appointment> {
    const response = await api.patch<Appointment>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar cita
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${this.basePath}/${id}`);
  }

  /**
   * Obtener citas del calendario (solo reservadas)
   */
  async getCalendar(params?: AppointmentCalendarParams): Promise<AppointmentSlot[]> {
    const response = await api.get<AppointmentSlot[]>(`${this.basePath}/calendar`, {
      params,
    });
    return response.data;
  }
}

export const appointmentService = new AppointmentService();
export default appointmentService;
